<!-- eslint-disable vuejs-accessibility/form-control-has-label -->
<template>
  <div
    class="bv-no-focus-ring"
    :class="[{ 'btn-group': buttons, 'btn-group-toggle': buttons, [`btn-group-${size}`] : size && buttons, 'split-radio-buttons': splitButtons}]"
    role="radiogroup"
    tabindex="-1"
  >
    <BFormRadio
      v-for="option, index in formOptions"
      :key="index"
      v-model="localValue"
      :value="option.value"
      :disabled="option.disabled || disabled"
      :name="groupName"
      group
      :stacked="stacked"
      :button="buttons"
      :plain="plain"
      :button-variant="buttonVariant"
      :size="size"
      :checked="option.value === localValue"
    >
      {{ option.text }}
    </BFormRadio>
  </div>
</template>

<script>
import BFormRadio from './BFormRadio.vue'

export const isPlainObject = obj => Object.prototype.toString.call(obj) === '[object Object]'

export default {
  name: 'BFormRadioGroup',
  components: {
    BFormRadio
  },
  props: {
    buttons: {
      type: Boolean,
      default: null
    },
    modelValue: {
      type: [String, Number, Array, Boolean],
      default: undefined
    },
    name: {
      type: String,
      default: null
    },
    size: {
      type: String,
      default: 'sm'
    },
    options: {
      type: Array,
      default: () => []
    },
    textField: {
      type: String,
      default: 'text'
    },
    valueField: {
      type: String,
      default: 'value'
    },
    disabledField: {
      type: String,
      default: 'disabled'
    },
    buttonVariant: {
      type: String,
      default: 'secondary'
    },
    disabled: Boolean,
    splitButtons: Boolean,
    stacked: Boolean,
    plain: Boolean
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      groupName: this.name || `__BVID__${this.$uuid}`
    }
  },
  computed: {
    formOptions() {
      return this.normalizeOptions(this.options)
    },
    localValue: {
      get() {
        return this.modelValue !== undefined ? this.modelValue : this.options[0][this.valueField]
      },
      set(value) {
        this.$emit('update:modelValue', value)
        this.$emit('change', value)
      }
    }
  },
  methods: {
    normalizeOption(option, key = null) {
      // When the option is an object, normalize it
      if (isPlainObject(option)) {
        const value = option[this.valueField]
        const text = option[this.textField]
        return {
          value: value === undefined ? key || text : value,
          text: String(text === undefined ? key : text),
          disabled: Boolean(option[this.disabledField])
        }
      }
      // Otherwise create an `<option>` object from the given value
      return {
        value: key || option,
        text: String(option),
        disabled: false
      }
    },
    normalizeOptions(options) {
      // Normalize the given options array
      if (Array.isArray(options)) {
        return options.map(option => this.normalizeOption(option))
      } else if (isPlainObject(options)) {
        // Normalize a `options` object to an array of options
        return Object.keys(options).map(key => this.normalizeOption(options[key] || {}, key))
      }
      // If not an array or object, return an empty array
      return []
    }
  }
}
</script>

<style lang="scss">
@import "~@ui/styles/src/scss/utils/lib";

.split-radio-buttons {
  label.btn:first-child {
    border-top-left-radius: 3px;
    border-bottom-left-radius: 3px;
  }

  label.btn:last-child {
    border-top-right-radius: 3px;
    border-bottom-right-radius: 3px;
  }

  label.btn {
    color: $primaryColor;
    background-color: #fff;
    border-color: $primaryColor;
    cursor: pointer;

    &:hover {
      color: #fff !important;
      background-color: $primaryColor !important;
    }

    &.active,
    &:active,
    &.focus,
    &:focus {
      background-color: $primaryColor !important;
      box-shadow: none;
      cursor: default;
    }
  }
}
</style>
